import BaseComponent from './BaseComponent';
import { gsap } from 'gsap';
import { getKeyboardFocusableElements, trapFocus } from '../utils';

export default class ParentMenu extends BaseComponent {
	constructor(elm, menu) {
		super();

		this.menu = menu;
		this.elm = elm;

		this.isOpen = false;

		this.breakpoint = this.menu.breakpoint;
		this.reduceMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

		this.parentMenuToggle = this.elm.querySelector('[data-menu-parent-toggle]');
		this.submenu = this.elm.querySelector('[data-menu-submenu]');
		this.submenuInner = this.elm.querySelector('[data-menu-submenu-inner]');
		this.submenuHeaderLinks = this.elm.querySelectorAll('[data-menu-submenu-header-link]');
		this.submenuLinks = this.elm.querySelectorAll('[data-menu-submenu-links]');

		this.trapFocusInstance = null;

		this.setInitialState();

		// Listeners
		this.breakpoint.addEventListener('change', () => {
			this.setInitialState();
		});

		this.isHoveringParent = false;
		this.leaveTimeout = null;

		this.elm.addEventListener('mouseenter', () => {
			this.isHoveringParent = true;
			this.checkMouseEnter();
		});

		this.elm.addEventListener('mouseleave', () => {
			this.isHoveringParent = false;
			this.checkMouseLeave();
		});

		if (this.breakpoint.matches) {
			this.parentMenuToggle.addEventListener('click', () => {
				this.open();
			});
		}
	}

	setInitialState() {
		if (this.breakpoint.matches) {
			gsap.set(this.submenu, {
				autoAlpha: 0,
			});

			gsap.set(this.submenuLinks, {
				autoAlpha: 0,
				pointerEvents: 'none',
			});

			gsap.set(this.submenuHeaderLinks, {
				autoAlpha: 0,
				pointerEvents: 'none',
			});

			this.elm.classList.remove('is-open');
			this.parentMenuToggle.setAttribute('aria-expanded', 'false');
			this.menu.header.elm.classList.remove('is-open');
		}
	}

	checkMouseEnter() {
		if (this.breakpoint.matches || this.isHoveringParent) {
			this.open();
		}
	}

	checkMouseLeave() {
		if (this.breakpoint.matches && !this.isHoveringParent) {
			this.close();
		}
	}

	open() {
		if (this.breakpoint.matches) {
			this.isOpen = true;

			this.menu.closeOtherParentMenus(this);

			this.parentMenuToggle.setAttribute('aria-expanded', 'true');

			clearTimeout(this.menu.closeBackgroundTimer);
			this.menu.animateDesktopBackground(this.submenuInner.offsetHeight + this.menu.header.elm.offsetHeight);

			gsap.set(this.submenu, {
				pointerEvents: 'all',
			});

			this.elm.classList.add('is-open');
			this.menu.header.elm.classList.add('is-open');

			const focusableElements = getKeyboardFocusableElements(this.submenu);
			if (this.trapFocusInstance) {
				this.trapFocusInstance.remove();
			}

			this.trapFocusInstance = trapFocus(this.submenu, focusableElements);

			const tl = gsap.timeline();

			tl.to(
				this.submenu,
				{
					autoAlpha: 1,
					duration: this.reduceMotionMediaQuery.matches ? 0 : 0.3,
					ease: 'sine.out',
				},
				'<'
			);

			tl.to(
				[this.submenuLinks, this.submenuHeaderLinks],
				{
					autoAlpha: 1,
					duration: this.reduceMotionMediaQuery.matches ? 0 : 0.3,
					ease: 'sine.out',
					pointerEvents: 'all',
				},
				0.2
			);
		}
	}

	close() {
		if (!this.isOpen) return;
		this.isOpen = false;

		this.menu.closeBackgroundTimer = setTimeout(() => {
			this.menu.animateDesktopBackground(0);
		}, 300);

		gsap.set(this.submenu, {
			pointerEvents: 'none',
		});

		if (this.trapFocusInstance) {
			this.trapFocusInstance.remove();
			this.trapFocusInstance = null;
		}

		this.elm.classList.remove('is-open');
		this.parentMenuToggle.setAttribute('aria-expanded', 'false');

		const tl = gsap.timeline({
			onComplete: () => {
				const anyOpen = this.menu.parentMenus.some((parentMenu) => parentMenu.isOpen);
				if (!anyOpen) {
					this.menu.header.elm.classList.remove('is-open');
				}
			},
		});

		tl.to(this.submenu, {
			autoAlpha: 0,
			duration: this.reduceMotionMediaQuery.matches ? 0 : 0.3,
			ease: 'none',
		});

		tl.to(
			[this.submenuLinks, this.submenuHeaderLinks],
			{
				autoAlpha: 0,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.3,
				ease: 'sine.out',
				pointerEvents: 'none',
			},
			'<'
		);
	}
}
